import {
  type InfiniteData,
  infiniteQueryOptions,
  QueryClient,
  type QueryKey,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type ActivityFilter,
  ApiError,
  type ApiToken,
  type Audience,
  client,
  type FileNode,
  type Group,
  type Principal,
  type ShareCreate,
  type ShareLink,
  type TokenCreate,
  unwrap,
} from "../api.ts";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (failures, error) =>
        !(error instanceof ApiError && error.status < 500) && failures < 2,
    },
  },
});

type NodePage = { items: FileNode[]; nextCursor: string | null };
type NodeLists = InfiniteData<NodePage>;

export const TRASH_RETENTION_DAYS = 30;

/** A folder the drive has not confirmed yet; its row waits instead of linking. */
export const PENDING_PREFIX = "pending:";

export const keys = {
  me: ["me"] as const,
  usage: ["usage"] as const,
  allNodes: ["nodes"] as const,
  nodes: (parentId: string | null) => ["nodes", parentId ?? "root"] as const,
  node: (id: string) => ["node", id] as const,
  nodeUsage: (id: string) => ["node", id, "usage"] as const,
  versions: (id: string) => ["node", id, "versions"] as const,
  audience: (id: string) => ["node", id, "audience"] as const,
  shares: (id: string) => ["node", id, "shares"] as const,
  allSearches: ["search"] as const,
  search: (q: string) => ["search", q] as const,
  trash: ["trash"] as const,
  groups: ["groups"] as const,
  group: (id: string) => ["groups", id] as const,
  tokens: ["tokens"] as const,
  token: (id: string) => ["tokens", id] as const,
  allActivity: ["activity"] as const,
  activity: (filter: ActivityFilter | undefined, nodeId: string | undefined) =>
    ["activity", filter ?? "all", nodeId ?? "all"] as const,
};

export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: () => unwrap(client.GET("/me")),
  staleTime: Number.POSITIVE_INFINITY,
});

export const usageQuery = queryOptions({
  queryKey: keys.usage,
  queryFn: () => unwrap(client.GET("/usage")),
  staleTime: 30_000,
});

export const nodeUsageQuery = (id: string) =>
  queryOptions({
    queryKey: keys.nodeUsage(id),
    queryFn: () => unwrap(client.GET("/nodes/{id}/usage", { params: { path: { id } } })),
    staleTime: 30_000,
  });

export const nodesQuery = (parentId: string | null) =>
  infiniteQueryOptions({
    queryKey: keys.nodes(parentId),
    queryFn: ({ pageParam }) =>
      unwrap(
        client.GET("/nodes", {
          params: {
            query: {
              ...(parentId === null ? {} : { parent: parentId }),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  });

export const searchQuery = (q: string) =>
  queryOptions({
    queryKey: keys.search(q),
    queryFn: () => unwrap(client.GET("/search", { params: { query: { q } } })),
  });

export const trashQuery = infiniteQueryOptions({
  queryKey: keys.trash,
  queryFn: ({ pageParam }) =>
    unwrap(client.GET("/trash", { params: { query: pageParam ? { cursor: pageParam } : {} } })),
  initialPageParam: null as string | null,
  getNextPageParam: (page) => page.nextCursor,
});

const listEntries = (qc: QueryClient) => qc.getQueriesData<NodeLists>({ queryKey: keys.allNodes });

const cachedNode = (id: string): { key: QueryKey; node: FileNode } | undefined => {
  for (const [key, data] of listEntries(queryClient)) {
    const node = data?.pages.flatMap((page) => page.items).find((item) => item.id === id);
    if (node) return { key, node };
  }
  return undefined;
};

export const nodeQuery = (id: string) =>
  queryOptions({
    queryKey: keys.node(id),
    queryFn: () => unwrap(client.GET("/nodes/{id}", { params: { path: { id } } })),
    initialData: () => cachedNode(id)?.node,
    initialDataUpdatedAt: () => {
      const hit = cachedNode(id);
      return hit ? queryClient.getQueryState(hit.key)?.dataUpdatedAt : undefined;
    },
  });

export const versionsQuery = (id: string) =>
  queryOptions({
    queryKey: keys.versions(id),
    queryFn: () => unwrap(client.GET("/nodes/{id}/versions", { params: { path: { id } } })),
  });

export const audienceQuery = (id: string) =>
  queryOptions({
    queryKey: keys.audience(id),
    queryFn: () => unwrap(client.GET("/nodes/{id}/audience", { params: { path: { id } } })),
  });

export const sharesQuery = (id: string) =>
  queryOptions({
    queryKey: keys.shares(id),
    queryFn: () => unwrap(client.GET("/nodes/{id}/shares", { params: { path: { id } } })),
  });

const storeShare = (qc: QueryClient, share: ShareLink) => {
  qc.setQueryData<{ items: ShareLink[] }>(keys.shares(share.nodeId), (data) =>
    data
      ? {
          items: data.items.some((item) => item.token === share.token)
            ? data.items.map((item) => (item.token === share.token ? share : item))
            : [share, ...data.items],
        }
      : data,
  );
};

export const useCreateShare = (nodeId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (share: Omit<ShareCreate, "nodeId">) =>
      unwrap(client.POST("/shares", { body: { ...share, nodeId } })),
    onSuccess: (share) => storeShare(qc, share),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.shares(nodeId) });
      void qc.invalidateQueries({ queryKey: keys.allActivity });
    },
  });
};

export const useRevokeShare = (nodeId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      unwrap(client.DELETE("/shares/{token}", { params: { path: { token } } })),
    onSuccess: (share) => storeShare(qc, share),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.shares(nodeId) });
      void qc.invalidateQueries({ queryKey: keys.allActivity });
    },
  });
};

const mapList = (qc: QueryClient, key: QueryKey, change: (items: FileNode[]) => FileNode[]) => {
  qc.setQueryData<NodeLists>(key, (data) =>
    data
      ? { ...data, pages: data.pages.map((page) => ({ ...page, items: change(page.items) })) }
      : data,
  );
};

const patchNodeCaches = (qc: QueryClient, id: string, change: (node: FileNode) => FileNode) => {
  qc.setQueryData<FileNode>(keys.node(id), (node) => (node ? change(node) : node));
  for (const [key] of listEntries(qc)) {
    mapList(qc, key, (items) => items.map((node) => (node.id === id ? change(node) : node)));
  }
};

const dropFromLists = (qc: QueryClient, id: string) => {
  for (const [key] of listEntries(qc)) {
    mapList(qc, key, (items) => items.filter((node) => node.id !== id));
  }
};

const snapshot = (qc: QueryClient, id: string) => {
  const node = qc.getQueryData<FileNode>(keys.node(id));
  const audience = qc.getQueryData<Audience>(keys.audience(id));
  const lists = listEntries(qc);
  return () => {
    qc.setQueryData(keys.node(id), node);
    qc.setQueryData(keys.audience(id), audience);
    for (const [key, data] of lists) qc.setQueryData(key, data);
  };
};

export const storeNode = (qc: QueryClient, node: FileNode) => {
  qc.setQueryData(keys.node(node.id), node);
  patchNodeCaches(qc, node.id, () => node);
};

/** Folder pages show their own child count, so the parents of a change refetch too. */
const refreshListings = (qc: QueryClient, ...parents: (string | null | undefined)[]) => {
  void qc.invalidateQueries({ queryKey: keys.allNodes });
  void qc.invalidateQueries({ queryKey: keys.allSearches });
  void qc.invalidateQueries({ queryKey: keys.usage });
  void qc.invalidateQueries({ queryKey: keys.allActivity });
  // Bytes move under every ancestor, not just the folder that changed.
  void qc.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "node" && query.queryKey[2] === "usage",
  });
  for (const parent of new Set(parents)) {
    if (parent) void qc.invalidateQueries({ queryKey: keys.node(parent), exact: true });
  }
};

export const refreshAfterUpload = (qc: QueryClient, parentId: string | null) =>
  refreshListings(qc, parentId);

/** Distinct people a set of principals reaches, excluding the owner. */
export const peopleReached = (
  principals: Principal[],
  groups: Group[] | undefined,
  owner: string | undefined,
): string[] => {
  const people = new Set<string>();
  for (const principal of principals) {
    if (principal.kind === "email") people.add(principal.email);
    if (principal.kind === "group") {
      const group = groups?.find((g) => g.id === principal.groupId);
      for (const email of group?.members ?? []) people.add(email);
    }
  }
  if (owner) people.delete(owner);
  return [...people];
};

export const useSetAudience = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `acl:${id}` },
    mutationFn: (principals: Principal[]) =>
      unwrap(
        client.PUT("/nodes/{id}/acl", {
          params: { path: { id } },
          body: principals.length > 0 ? { mode: "set", principals } : { mode: "inherit" },
        }),
      ),
    onMutate: async (principals) => {
      const restore = snapshot(qc, id);
      await qc.cancelQueries({ queryKey: keys.audience(id) });
      const mode = principals.length > 0 ? "set" : "inherit";
      const previous = qc.getQueryData<Audience>(keys.audience(id));
      const groups = qc.getQueryData<{ items: Group[] }>(keys.groups)?.items;
      const emails = peopleReached(principals, groups, undefined);
      const isPublic = principals.some((p) => p.kind === "public");
      // An ancestor can narrow what this node offers, and only the drive resolves that.
      const narrowed = (previous?.restrictedBy ?? []).some((node) => node.id !== id);
      const effective = narrowed
        ? (previous?.effective ?? { public: isPublic, emails })
        : { public: isPublic, emails };
      qc.setQueryData<Audience>(keys.audience(id), {
        mode,
        principals,
        effective,
        restrictedBy: previous?.restrictedBy ?? [],
      });
      patchNodeCaches(qc, id, (node) => ({
        ...node,
        aclMode: mode,
        viewerCount: effective.emails.length,
        public: effective.public,
      }));
      return { restore };
    },
    onError: (_error, _principals, context) => context?.restore(),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.node(id) });
      void qc.invalidateQueries({ queryKey: keys.allNodes });
      void qc.invalidateQueries({ queryKey: keys.groups });
    },
  });
};

export const groupsQuery = queryOptions({
  queryKey: keys.groups,
  queryFn: () => unwrap(client.GET("/groups")),
});

export const groupQuery = (id: string) =>
  queryOptions({
    queryKey: keys.group(id),
    queryFn: () => unwrap(client.GET("/groups/{id}", { params: { path: { id } } })),
    initialData: () =>
      queryClient.getQueryData<{ items: Group[] }>(keys.groups)?.items.find((g) => g.id === id),
    initialDataUpdatedAt: () => queryClient.getQueryState(keys.groups)?.dataUpdatedAt,
  });

const storeGroup = (qc: QueryClient, group: Group) => {
  qc.setQueryData(keys.group(group.id), group);
  qc.setQueryData<{ items: Group[] }>(keys.groups, (data) =>
    data
      ? {
          items: data.items.some((g) => g.id === group.id)
            ? data.items.map((g) => (g.id === group.id ? group : g))
            : [...data.items, group].sort((a, b) => a.name.localeCompare(b.name)),
        }
      : data,
  );
};

export const useCreateGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => unwrap(client.POST("/groups", { body: { name, members: [] } })),
    onSuccess: (group) => storeGroup(qc, group),
  });
};

export const usePatchGroup = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `group:${id}` },
    mutationFn: (patch: { name?: string; members?: string[] }) =>
      unwrap(client.PATCH("/groups/{id}", { params: { path: { id } }, body: patch })),
    onMutate: async (patch) => {
      const group = qc.getQueryData<Group>(keys.group(id));
      const groups = qc.getQueryData<{ items: Group[] }>(keys.groups);
      if (patch.members && group) {
        await qc.cancelQueries({ queryKey: keys.group(id), exact: true });
        storeGroup(qc, { ...group, members: [...patch.members].sort() });
      }
      return {
        restore: () => {
          qc.setQueryData(keys.group(id), group);
          qc.setQueryData(keys.groups, groups);
        },
      };
    },
    onError: (_error, _patch, context) => context?.restore(),
    onSuccess: (group) => storeGroup(qc, group),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.groups });
      void qc.invalidateQueries({ queryKey: ["node"] });
      void qc.invalidateQueries({ queryKey: keys.allNodes });
    },
  });
};

export const useDeleteGroup = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(client.DELETE("/groups/{id}", { params: { path: { id } } })),
    onSuccess: () => {
      qc.setQueryData<{ items: Group[] }>(keys.groups, (data) =>
        data ? { items: data.items.filter((g) => g.id !== id) } : data,
      );
      void qc.invalidateQueries({ queryKey: keys.groups, exact: true });
    },
  });
};

const usePatchNode = (id: string, optimistic: boolean) => {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `patch:${id}` },
    mutationFn: (patch: { name?: string; sandboxed?: boolean; currentVersion?: number }) =>
      unwrap(client.PATCH("/nodes/{id}", { params: { path: { id } }, body: patch })),
    onMutate: async (patch) => {
      const restore = snapshot(qc, id);
      if (optimistic) {
        await qc.cancelQueries({ queryKey: keys.node(id), exact: true });
        patchNodeCaches(qc, id, (node) => ({ ...node, ...patch }));
      }
      return { restore };
    },
    onError: (_error, _patch, context) => {
      if (optimistic) context?.restore();
    },
    onSuccess: (node) => storeNode(qc, node),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.node(id) });
      void qc.invalidateQueries({ queryKey: keys.allNodes });
      void qc.invalidateQueries({ queryKey: keys.allSearches });
      void qc.invalidateQueries({ queryKey: keys.usage });
      void qc.invalidateQueries({ queryKey: keys.allActivity });
    },
  });
};

export const useRename = (id: string) => usePatchNode(id, false);
export const useSandbox = (id: string) => usePatchNode(id, true);
export const useRollback = (id: string) => usePatchNode(id, false);

export const useDeleteVersion = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `patch:${id}` },
    mutationFn: (version: number) =>
      unwrap(
        client.DELETE("/nodes/{id}/versions/{version}", { params: { path: { id, version } } }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.versions(id) });
      void qc.invalidateQueries({ queryKey: keys.node(id) });
      void qc.invalidateQueries({ queryKey: keys.allNodes });
      void qc.invalidateQueries({ queryKey: keys.usage });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.allActivity }),
  });
};

export const useCreateFolder = (parentId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      unwrap(client.POST("/nodes", { body: { kind: "folder", name, parentId } })),
    onMutate: async (name) => {
      await qc.cancelQueries({ queryKey: keys.nodes(parentId) });
      const lists = listEntries(qc);
      const pending: FileNode = {
        id: `${PENDING_PREFIX}${crypto.randomUUID()}`,
        kind: "folder",
        name,
        parentId,
        ancestors: [],
        childCount: 0,
        contentType: null,
        size: 0,
        currentVersion: null,
        durationMs: null,
        hasPeaks: false,
        hasThumb: false,
        versionCount: 0,
        sandboxed: false,
        aclMode: "inherit",
        viewerCount: 0,
        public: false,
        url: "",
        deletedAt: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      mapList(qc, keys.nodes(parentId), (items) => [pending, ...items]);
      return {
        pendingId: pending.id,
        restore: () => {
          for (const [key, data] of lists) qc.setQueryData(key, data);
        },
      };
    },
    onError: (_error, _name, context) => context?.restore(),
    onSuccess: (node, _name, context) => {
      if (context) dropFromLists(qc, context.pendingId);
      qc.setQueryData(keys.node(node.id), node);
      mapList(qc, keys.nodes(parentId), (items) =>
        items.some((item) => item.id === node.id) ? items : [node, ...items],
      );
    },
    onSettled: () => refreshListings(qc, parentId),
  });
};

export type NodeMove = {
  id: string;
  from: string | null;
  parentId: string | null;
  confirmWidening?: boolean;
};

/** With an id, moves of that node queue behind its other patches. */
export const useMoveNode = (id?: string) => {
  const qc = useQueryClient();
  return useMutation({
    ...(id ? { scope: { id: `patch:${id}` } } : {}),
    mutationFn: ({ id, parentId, confirmWidening }: NodeMove) =>
      unwrap(
        client.PATCH("/nodes/{id}", {
          params: { path: { id } },
          body: { parentId, ...(confirmWidening ? { confirmWidening: true } : {}) },
        }),
      ),
    onSuccess: (node, move) => {
      dropFromLists(qc, move.id);
      storeNode(qc, node);
    },
    onSettled: (node, _error, move) => {
      refreshListings(qc, move.from, move.parentId, node?.parentId);
      void qc.invalidateQueries({ queryKey: keys.node(move.id) });
      void qc.invalidateQueries({ queryKey: keys.audience(move.id) });
    },
  });
};

export const useDeleteNode = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(client.DELETE("/nodes/{id}", { params: { path: { id } } })),
    onSuccess: () => {
      const parent = qc.getQueryData<FileNode>(keys.node(id))?.parentId;
      dropFromLists(qc, id);
      refreshListings(qc, parent);
      void qc.invalidateQueries({ queryKey: keys.trash });
    },
  });
};

export const useRestoreNode = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(client.POST("/nodes/{id}/restore", { params: { path: { id } } })),
    onSuccess: (node) => {
      qc.setQueryData<NodeLists>(keys.trash, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items.filter((item) => item.id !== id),
              })),
            }
          : data,
      );
      qc.setQueryData(keys.node(node.id), node);
      refreshListings(qc, node.parentId);
      void qc.invalidateQueries({ queryKey: keys.trash });
    },
  });
};

export const usePurgeNode = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(client.DELETE("/trash/{id}", { params: { path: { id } } })),
    onSuccess: () => {
      qc.removeQueries({ queryKey: keys.node(id) });
      void qc.invalidateQueries({ queryKey: keys.trash });
      void qc.invalidateQueries({ queryKey: keys.usage });
    },
  });
};

export const useEmptyTrash = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(client.DELETE("/trash")),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.trash });
      void qc.invalidateQueries({ queryKey: keys.usage });
    },
  });
};

export const tokensQuery = queryOptions({
  queryKey: keys.tokens,
  queryFn: () => unwrap(client.GET("/tokens")),
});

export const tokenQuery = (id: string) =>
  queryOptions({
    queryKey: keys.token(id),
    queryFn: () => unwrap(client.GET("/tokens/{id}", { params: { path: { id } } })),
    initialData: () =>
      queryClient.getQueryData<{ items: ApiToken[] }>(keys.tokens)?.items.find((t) => t.id === id),
    initialDataUpdatedAt: () => queryClient.getQueryState(keys.tokens)?.dataUpdatedAt,
  });

const storeToken = (qc: QueryClient, token: ApiToken) => {
  qc.setQueryData(keys.token(token.id), token);
  qc.setQueryData<{ items: ApiToken[] }>(keys.tokens, (data) =>
    data
      ? {
          items: data.items.some((t) => t.id === token.id)
            ? data.items.map((t) => (t.id === token.id ? token : t))
            : [token, ...data.items],
        }
      : data,
  );
};

/** Resolves to the created token including its secret, which the drive never shows again. */
export const useCreateToken = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: TokenCreate) => unwrap(client.POST("/tokens", { body: token })),
    onSuccess: ({ secret: _secret, ...token }) => storeToken(qc, token),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.tokens });
      void qc.invalidateQueries({ queryKey: keys.allActivity });
    },
  });
};

export const useRevokeToken = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(client.DELETE("/tokens/{id}", { params: { path: { id } } })),
    onSuccess: (token) => storeToken(qc, token),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.tokens });
      void qc.invalidateQueries({ queryKey: keys.allActivity });
    },
  });
};

export const activityQuery = (filter?: ActivityFilter, nodeId?: string) =>
  infiniteQueryOptions({
    queryKey: keys.activity(filter, nodeId),
    queryFn: ({ pageParam }) =>
      unwrap(
        client.GET("/access-log", {
          params: {
            query: {
              ...(filter ? { actor: filter } : {}),
              ...(nodeId ? { node: nodeId } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    staleTime: 10_000,
  });

export const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : "Something went wrong.";

export const codeOf = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;
